// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/abrParser.js
//
// Photoshop .abr sampled-brush reader (the layout GIMP and Krita read):
//   v1/v2   u16 version, u16 count, then per brush u16 type + u32 size.
//           Type 2 = sampled: misc, spacing %, (v2) UCS-2 name, bounds,
//           depth, compression, pixels.
//   v6/7/10 u16 version, u16 subversion, then 8BIM sections; the "samp"
//           section holds the tips. Each tip skips a key block (47 bytes
//           for subversion 1, 301 for 2) before bounds/depth/compression.
// Pixels are 8- or 16-bit greyscale masks (255 = full paint), raw or
// PackBits RLE per scanline. Brush dynamics live in the "desc" descriptor
// section and are not read; callers report them as missing.

export const ABR_LIMITS = Object.freeze({
  maxBrushes: 2000,
  maxDimension: 5000,
  maxPixelsPerBrush: 25 * 1024 * 1024,
  maxTotalPixels: 160 * 1024 * 1024,
});

class Reader {
  constructor(bytes) {
    this.bytes = bytes;
    this.pos = 0;
  }

  need(count) {
    if (count < 0 || this.pos + count > this.bytes.length) throw new Error('ABR data ends unexpectedly');
  }

  u8() {
    this.need(1);
    return this.bytes[this.pos++];
  }

  i8() {
    const v = this.u8();
    return v > 127 ? v - 256 : v;
  }

  u16() {
    this.need(2);
    const v = (this.bytes[this.pos] << 8) | this.bytes[this.pos + 1];
    this.pos += 2;
    return v;
  }

  u32() {
    this.need(4);
    const b = this.bytes;
    const v = ((b[this.pos] << 24) >>> 0) + (b[this.pos + 1] << 16) + (b[this.pos + 2] << 8) + b[this.pos + 3];
    this.pos += 4;
    return v;
  }

  i32() {
    const v = this.u32();
    return v > 0x7fffffff ? v - 0x100000000 : v;
  }

  skip(count) {
    this.need(count);
    this.pos += count;
  }

  ascii(count) {
    this.need(count);
    let out = '';
    for (let i = 0; i < count; i += 1) out += String.fromCharCode(this.bytes[this.pos + i]);
    this.pos += count;
    return out;
  }
}

function decodeRle(reader, width, height, bytesPerPixel) {
  const rowBytes = width * bytesPerPixel;
  const lineLengths = [];
  for (let y = 0; y < height; y += 1) lineLengths.push(reader.u16());
  const out = new Uint8Array(rowBytes * height);
  for (let y = 0; y < height; y += 1) {
    const end = reader.pos + lineLengths[y];
    reader.need(lineLengths[y]);
    let x = 0;
    const base = y * rowBytes;
    while (reader.pos < end && x < rowBytes) {
      const n = reader.i8();
      if (n >= 0) {
        const available = Math.min(n + 1, end - reader.pos);
        const count = Math.min(available, rowBytes - x);
        out.set(reader.bytes.subarray(reader.pos, reader.pos + count), base + x);
        reader.pos += available;
        x += count;
      } else if (n > -128) {
        const value = reader.u8();
        const count = Math.min(1 - n, rowBytes - x);
        out.fill(value, base + x, base + x + count);
        x += count;
      }
    }
    reader.pos = end;
  }
  return out;
}

function toEightBit(data, width, height, depth) {
  if (depth === 8) return data;
  const out = new Uint8Array(width * height);
  for (let i = 0; i < out.length; i += 1) out[i] = data[i * 2];
  return out;
}

function readPixels(reader, width, height, depth, compressed, budget) {
  if (width <= 0 || height <= 0 || width > ABR_LIMITS.maxDimension || height > ABR_LIMITS.maxDimension) throw new Error('ABR brush has invalid dimensions');
  if (width * height > ABR_LIMITS.maxPixelsPerBrush) throw new Error('ABR brush is too large');
  budget.pixels += width * height;
  if (budget.pixels > ABR_LIMITS.maxTotalPixels) throw new Error('ABR file holds too many brush pixels');
  if (depth !== 8 && depth !== 16) throw new Error('Unsupported ABR bit depth ' + depth);
  const bytesPerPixel = depth / 8;
  let data;
  if (compressed) {
    data = decodeRle(reader, width, height, bytesPerPixel);
  } else {
    const size = width * height * bytesPerPixel;
    reader.need(size);
    data = reader.bytes.slice(reader.pos, reader.pos + size);
    reader.pos += size;
  }
  return toEightBit(data, width, height, depth);
}

function readUcs2(reader) {
  const length = reader.u32();
  if (length > 1024) throw new Error('ABR brush name too long');
  let name = '';
  for (let i = 0; i < length; i += 1) {
    const code = reader.u16();
    if (code) name += String.fromCharCode(code);
  }
  return name;
}

function parseV12(reader, version, budget) {
  const count = reader.u16();
  const brushes = [];
  const warnings = [];
  for (let index = 0; index < count && index < ABR_LIMITS.maxBrushes; index += 1) {
    const type = reader.u16();
    const size = reader.u32();
    const start = reader.pos;
    const end = start + size;
    if (end > reader.bytes.length) throw new Error('ABR brush extends past the file');
    if (type !== 2) {
      warnings.push(`brush ${index + 1}: computed (parametric) brush skipped`);
      reader.pos = end;
      continue;
    }
    try {
      reader.u32();
      const spacing = reader.u16();
      const name = version === 2 ? readUcs2(reader) : '';
      reader.skip(1);
      reader.skip(8);
      const top = reader.i32();
      const left = reader.i32();
      const bottom = reader.i32();
      const right = reader.i32();
      const depth = reader.u16();
      const compressed = reader.u8() !== 0;
      const width = right - left;
      const height = bottom - top;
      const pixels = readPixels(reader, width, height, depth, compressed, budget);
      brushes.push({ name: name || `ABR brush ${index + 1}`, width, height, pixels, spacing: spacing > 0 ? spacing / 100 : 0.25, hasSpacing: spacing > 0 });
    } catch (err) {
      warnings.push(`brush ${index + 1}: ${err.message}`);
    }
    reader.pos = end;
  }
  return { brushes, warnings };
}

function parseV6(reader, budget) {
  const subVersion = reader.u16();
  const brushes = [];
  const warnings = [];
  const sections = [];
  while (reader.pos + 12 <= reader.bytes.length) {
    const signature = reader.ascii(4);
    if (signature !== '8BIM' && signature !== '8B64') break;
    const tag = reader.ascii(4);
    const size = reader.u32();
    const start = reader.pos;
    const end = start + size;
    if (end > reader.bytes.length) {
      warnings.push(`section ${tag} truncated`);
      break;
    }
    sections.push(tag);
    if (tag === 'samp') {
      while (reader.pos + 4 < end && brushes.length < ABR_LIMITS.maxBrushes) {
        const brushSize = reader.u32();
        let brushEnd = reader.pos + brushSize;
        const complement = 4 - (brushSize % 4);
        if (complement < 4) brushEnd += complement;
        if (brushEnd > end + 4) {
          warnings.push('sampled brush extends past its section');
          break;
        }
        try {
          reader.skip(subVersion === 1 ? 47 : 301);
          const top = reader.i32();
          const left = reader.i32();
          const bottom = reader.i32();
          const right = reader.i32();
          const depth = reader.u16();
          const compressed = reader.u8() !== 0;
          const width = right - left;
          const height = bottom - top;
          const pixels = readPixels(reader, width, height, depth, compressed, budget);
          brushes.push({ name: `ABR brush ${brushes.length + 1}`, width, height, pixels, spacing: 0.25, hasSpacing: false });
        } catch (err) {
          warnings.push(`brush ${brushes.length + 1}: ${err.message}`);
        }
        reader.pos = Math.min(brushEnd, end);
      }
    }
    reader.pos = end;
  }
  if (!sections.includes('samp')) warnings.push('no sampled brushes section; computed-only ABR files have no tips to import');
  return { brushes, warnings, sections };
}

export function parseAbr(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || new ArrayBuffer(0));
  const reader = new Reader(bytes);
  const version = reader.u16();
  const budget = { pixels: 0 };
  if (version === 1 || version === 2) return { version, ...parseV12(reader, version, budget) };
  if (version === 6 || version === 7 || version === 10) return { version, ...parseV6(reader, budget) };
  throw new Error('Unsupported ABR version ' + version);
}
